import "./SurveyTakingForm.scss";

import {
  Alert,
  Button,
  Divider,
  ExclamationTriangleIcon,
  Flex,
  Input,
  Skeleton,
  Slider,
  Text,
} from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import FormState, { FieldDescriptor, FormData } from "@shopify/react-form-state";
import {
  SurveyDetailsBarInfo,
  TakingCommands,
  TakingSubmitData,
  detailsBarText,
  getQuestionFieldCommentId,
  getQuestionFieldId,
  getSurveyTypeDisplay,
} from "@teams/cards";
import { formatQuizPoints } from "@teams/client-shared";
import { TeamsSurveyResponse } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";

import { CheckboxField } from "../../../../_shared/components/fields";
import { isError } from "../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../_shared/services/SurveyApi";
import { getEditInfoText } from "../../../../_shared/utils/teamsHelpers";
import { QuestionVoteField } from "./QuestionVoteField";

type Props = {
  contextOrApiToken: app.Context | string;
  surveyId: string;
  hideHelp?: boolean;
  trackingSource: string;
  onCompleted: (survey: SurveyDTO, userResponse: TeamsSurveyResponse) => void;
};

export const SurveyTakingForm: React.FC<Props> = ({
  surveyId,
  onCompleted,
  hideHelp,
  contextOrApiToken,
  trackingSource,
}) => {
  const [questionIndex, setQuestionIndex] = useState<number>(0);
  const [startDate, setStartDate] = useState<Date>();
  const [survey, setSurvey] = useState<SurveyDTO>();
  const [userResponse, setUserResponse] = useState<TeamsSurveyResponse>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [error, setError] = useState<string>();
  const { search } = useLocation();

  const allowRetake = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get("allowRetake") === "true";
  }, [search]);

  const isLastQuestion = survey && questionIndex >= survey.questions.length - 1;

  const loadSurvey = useCallback(
    async (hideLoader?: boolean) => {
      if (!hideLoader) {
        setIsLoading(true);
      }

      const [surveyResponse, response] = await Promise.all([
        surveyApi.survey(contextOrApiToken, surveyId),
        surveyApi.surveyUserResponse(contextOrApiToken, surveyId),
      ]);

      if (isError(surveyResponse) || isError(response)) {
        setIsLoading(false);

        setError("Sorry, there was a problem loading this survey");
        return;
      }

      setSurvey(surveyResponse.survey);
      setUserResponse(response);
      setIsLoading(false);

      if (response?.completed && !allowRetake) {
        onCompleted(surveyResponse.survey, response);
      }
    },
    [contextOrApiToken, onCompleted, surveyId, allowRetake]
  );

  useEffect(() => {
    setStartDate(new Date());
    loadSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextOrApiToken, surveyId]);

  async function save(formFields: FormData<SurveyTakingFields>) {
    if (!survey) {
      return;
    }

    const body: TakingSubmitData = {
      command: TakingCommands.Submit,
      surveyId: survey.id as string,
      questionIndexId: currentQuestion.id,
      questionIds: [currentQuestion.id],
      direction: "next",
      startDate: startDate?.toISOString() as string,
    };
    if (survey.type === "qna") {
      body.responseAnonymity = formFields.fields.anonymousResponse.value ? "anonymous" : "none";
    }

    body[getQuestionFieldId(currentQuestion.id)] = formFields.fields[currentQuestion.id]
      .value as string;

    if (formFields.fields[getQuestionFieldCommentId(currentQuestion.id)]?.value) {
      body[getQuestionFieldCommentId(currentQuestion.id)] = formFields.fields[
        getQuestionFieldCommentId(currentQuestion.id)
      ].value as string;
    }

    const result = await surveyApi.vote(
      contextOrApiToken,
      survey.id as string,
      body,
      trackingSource
    );

    if (isError(result)) {
      console.error(JSON.stringify(result, null, 2));
      return [new Error(result.message || "Unknown error occured")];
    } else {
      setUserResponse(result.userResponse);
    }

    if (!allowRetake && !result.missingRequiredIds.length && result.userResponse.completed) {
      onCompleted(survey, result.userResponse);
    } else if (allowRetake && isLastQuestion) {
      onCompleted(survey, result.userResponse);
    }
  }

  const initialValues = useMemo(() => {
    let values: SurveyTakingFields = { anonymousResponse: false };

    if (!survey) {
      return values;
    }

    survey.questions.forEach(q => {
      const qr = userResponse?.responses.find(r => r.questionId === q.id);
      if (qr) {
        values[q.id] = Array.isArray(qr.value) ? qr.value.join(",") : qr.value;
        if (q.allowComments) {
          values[getQuestionFieldCommentId(q.id)] = qr.comment || "";
        }
      } else {
        values[q.id] = "";
        if (q.allowComments) {
          values[getQuestionFieldCommentId(q.id)] = "";
        }
      }
    });
    return values;
  }, [survey, userResponse]);

  if (error) {
    return <Alert content={error} danger icon={<ExclamationTriangleIcon />} />;
  }

  if (isLoading || !survey) {
    return (
      <div>
        <Skeleton animation="wave">
          <Flex column gap="gap.small">
            <Skeleton.Text />
            <Skeleton.Shape width="100%" height="200px" />
            <Flex.Item push>
              <Skeleton.Button />
            </Flex.Item>
          </Flex>
        </Skeleton>
      </div>
    );
  }

  if (survey.state !== "open") {
    return (
      <Alert
        danger
        icon={<ExclamationTriangleIcon size="large" />}
        content={
          <Text
            size="large"
            align="center"
            content={
              survey.state === "pending"
                ? `This ${getSurveyTypeDisplay(survey.type)} is not open for responses yet`
                : `This ${getSurveyTypeDisplay(survey.type)} is no longer open for responses`
            }
          />
        }
      />
    );
  }

  const currentQuestion = survey.questions[questionIndex];

  return (
    <FormState initialValues={initialValues} onSubmit={save}>
      {({ fields, submitting, submit, errors }) => {
        const showAddCommentInput =
          currentQuestion.allowComments && fields[currentQuestion.id].value !== "";

        return (
          <Flex column gap="gap.small" className="survey-taking-form">
            <Flex column gap="gap.medium">
              <Flex column gap="gap.small">
                {(survey.questions.length > 1 || survey.type === "quiz") && (
                  <>
                    <Text
                      content={survey.title}
                      align="start"
                      weight="bold"
                      size="large"
                      className="survey-taking-form__title"
                    />
                    <Text content={survey.description} align="start" weight="regular" />
                    <Divider fitted size={1} />
                  </>
                )}
                <Text
                  content={detailsBarText(survey as SurveyDetailsBarInfo, {
                    activeCardDateFormat: false,
                  })}
                />
              </Flex>

              {survey.appealInfoType && (
                <Text
                  className="survey-meeting-card__info-text"
                  content={getEditInfoText(survey?.appealInfoType)}
                />
              )}
              <Flex column>
                <Flex.Item grow>
                  <Text
                    weight="bold"
                    content={currentQuestion.label}
                    as="div"
                    align="start"
                    size="large"
                  />
                </Flex.Item>
                {currentQuestion.pointValue !== undefined && (
                  <Flex.Item push>
                    <Text content={`${formatQuizPoints(currentQuestion.pointValue)}`} />
                  </Flex.Item>
                )}
              </Flex>

              <Flex.Item grow>
                <div
                  className={
                    survey.type === "qna"
                      ? "survey-taking-form__qna-question"
                      : "survey-taking-form__question"
                  }
                >
                  <QuestionVoteField
                    key={currentQuestion.id}
                    question={currentQuestion}
                    field={fields[currentQuestion.id] as FieldDescriptor<string>}
                    onChoiceAddedSubmit={async (questionId, choice) => {
                      const result = await surveyApi.addChoiceToQuestion(
                        contextOrApiToken,
                        survey.id!,
                        questionId,
                        choice
                      );
                      if (!isError(result)) {
                        await loadSurvey(true);
                      } else {
                        return result;
                      }
                    }}
                  />
                </div>
              </Flex.Item>

              {survey.type === "qna" && survey.anonymity === "voluntary" && (
                <div>
                  <CheckboxField
                    labelPosition="end"
                    field={fields.anonymousResponse}
                    trueLabel="Submit anonymously"
                    falseLabel="Submit anonymously"
                  />
                </div>
              )}

              {showAddCommentInput && (
                <Input
                  autoComplete="off"
                  className="survey-taking-form__comment"
                  fluid
                  placeholder={currentQuestion.commentAppeal || "Additional comments (optional)"}
                  onChange={(_e, props) => {
                    if (!props) {
                      return;
                    }
                    fields[getQuestionFieldCommentId(currentQuestion.id)].onChange(
                      props.value || ""
                    );
                  }}
                  value={fields[getQuestionFieldCommentId(currentQuestion.id)].value as string}
                />
              )}

              <Flex hAlign="stretch" vAlign="end">
                {!hideHelp && (
                  <Flex.Item>
                    <a
                      href="https://www.polly.ai/help/microsoft-teams/polly-for-teams-meetings-guide"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Text content="What is this?" size="smaller" weight="light" />
                    </a>
                  </Flex.Item>
                )}
                <Flex.Item push>
                  <Flex gap="gap.small">
                    {questionIndex > 0 && (
                      <Button
                        secondary
                        disabled={submitting}
                        content="Previous"
                        onClick={() => {
                          setQuestionIndex(questionIndex - 1);
                        }}
                      />
                    )}
                    <Button
                      primary
                      loading={submitting}
                      disabled={
                        submitting ||
                        (currentQuestion.required && !fields[currentQuestion.id].value)
                      }
                      onClick={() => {
                        submit();
                        if (!isLastQuestion) {
                          setQuestionIndex(questionIndex + 1);
                        }
                      }}
                      content={isLastQuestion ? "Submit" : "Next"}
                    />
                  </Flex>
                </Flex.Item>
              </Flex>

              {survey.questions.length > 1 && (
                <Flex column>
                  <Text
                    content={`${questionIndex + 1} of ${survey.questions.length}`}
                    size="small"
                    weight="light"
                    align="center"
                  />
                  <Slider
                    fluid
                    value={questionIndex}
                    max={survey.questions.length - 1}
                    min={0}
                    readOnly
                  />
                </Flex>
              )}
            </Flex>

            {errors && errors.length > 0 && (
              <Flex column gap="gap.small">
                {errors.map((error, index) => (
                  <Alert key={index} danger>
                    {error.message || error.field}
                  </Alert>
                ))}
              </Flex>
            )}
          </Flex>
        );
      }}
    </FormState>
  );
};

type SurveyTakingFields = Record<string, string | boolean> & {
  anonymousResponse: boolean;
};
